import type { TransactionWithRelations } from '@/db/repositories/transactionRepository';
import { isFinancingPartialPaymentTransaction } from '@/lib/transactionNavigation';
import { roundCurrency } from '@/lib/installments';
import { getContractInstallmentAmount, type FinancingCurve } from '@/lib/financingCurve';
import { compareIso } from '@/lib/compareIso';

/**
 * Decomposição do que uma parcela pendente ainda cobra hoje. Nada disso é coluna:
 * o valor da linha é a verdade e a base vem do contrato, então `charges` e `paid`
 * são sempre reconstituíveis a partir dos lançamentos vivos. Ver ADR 0055.
 */
export interface InstallmentBreakdown {
    /** Valor de contrato da parcela — derivado da curva quando o contrato tem uma. */
    contractAmount: number;
    /** Soma dos pagamentos parciais já feitos nessa parcela. */
    paidAmount: number;
    /** O que a parcela cobraria sem encargos: contrato − pago. */
    baseAmount: number;
    /** Acréscimo manual (juros/multa) lançado na parcela — 0 quando não há. */
    chargesAmount: number;
}

/**
 * Quanto a parcela deve cobrar sem encargos. É a ÚNICA definição dessa regra —
 * o repositório e a UI leem daqui para não divergirem (o preview do sheet mostraria
 * um número e o banco gravaria outro).
 */
export function getInstallmentBaseAmount(monthlyAmount: number, alreadyPaid: number) {
    return Math.max(0, roundCurrency(monthlyAmount - alreadyPaid));
}

/** Quanto já foi pago parcialmente em cada número de parcela. */
export function sumPartialPaymentsByInstallment(
    transactions: TransactionWithRelations[],
): Map<number, number> {
    const map = new Map<number, number>();
    for (const item of transactions) {
        if (!isFinancingPartialPaymentTransaction(item)) continue;
        if (item.financingInstallmentNumber == null) continue;
        map.set(
            item.financingInstallmentNumber,
            roundCurrency((map.get(item.financingInstallmentNumber) ?? 0) + item.amount),
        );
    }
    return map;
}

/** Parcela pendente vista pelo planejador de adiantamento — só o que ele consome. */
export interface AdvanceCandidate {
    id: string;
    date: string;
    createdAt: string;
    amount: number;
    financingInstallmentNumber: number | null;
}

export interface AdvancePlan {
    /** Parcelas inteiramente cobertas pelo valor pago — saem da série. */
    settledIds: string[];
    settledNumbers: number[];
    /** Parcela que sobra parcialmente coberta; null quando o valor fechou em parcela cheia. */
    partialId: string | null;
    partialNumber: number | null;
    /** Quanto a `partialId` passa a cobrar. */
    partialAmount: number;
    /** Valor que não coube em parcela nenhuma — > 0 significa pagar mais que o pendente. */
    leftover: number;
}

/**
 * Distribui um pagamento extra sobre as PRÓXIMAS parcelas pendentes, na ordem de
 * vencimento: quita as que o valor cobre inteiras e deixa o resto na primeira que
 * sobreviver. É a amortização de empréstimo (ADR 0057) — quem adianta dinheiro num
 * contrato de parcela fixa deixa de pagar os próximos meses, não os últimos.
 *
 * É a ÚNICA definição dessa regra: o repositório grava a partir daqui e o sheet mostra
 * o mesmo cálculo antes de confirmar, então o preview não pode divergir do que é salvo.
 *
 * Por isso a ORDEM é decidida aqui dentro e não pelo chamador: as duas origens ordenam
 * empate de vencimento de formas opostas (o cronograma do contrato por `createdAt` asc, a
 * query de lançamentos da tela por `createdAt` desc). Duas parcelas pendentes no mesmo dia
 * com valores diferentes fariam o preview prometer uma coisa e o banco gravar outra.
 *
 * `amount` de cada linha é o que a parcela cobra HOJE — já com pagamento parcial
 * descontado e encargos somados.
 */
export function planAdvanceOnNextInstallments(
    pending: AdvanceCandidate[],
    amount: number,
): AdvancePlan {
    const ordered = [...pending].sort((left, right) =>
        compareIso(left.date, right.date)
        || compareIso(left.createdAt, right.createdAt),
    );

    const plan: AdvancePlan = {
        settledIds: [],
        settledNumbers: [],
        partialId: null,
        partialNumber: null,
        partialAmount: 0,
        leftover: 0,
    };

    let budget = roundCurrency(Math.max(0, amount));

    for (const installment of ordered) {
        if (budget <= 0) break;
        const owed = roundCurrency(installment.amount);
        // Parcela de valor zerado (tudo já pago parcialmente) sai junto: cobrar de novo
        // por ela seria pedir dinheiro que o contrato não deve mais.
        if (budget >= owed) {
            plan.settledIds.push(installment.id);
            if (installment.financingInstallmentNumber != null) {
                plan.settledNumbers.push(installment.financingInstallmentNumber);
            }
            budget = roundCurrency(budget - owed);
            continue;
        }
        plan.partialId = installment.id;
        plan.partialNumber = installment.financingInstallmentNumber;
        plan.partialAmount = roundCurrency(owed - budget);
        budget = 0;
    }

    plan.leftover = budget;
    return plan;
}

export function getInstallmentBreakdown(
    installment: Pick<TransactionWithRelations, 'amount' | 'financingInstallmentNumber'>,
    curve: FinancingCurve,
    partialPaidByInstallment: Map<number, number>,
): InstallmentBreakdown {
    const paidAmount = installment.financingInstallmentNumber != null
        ? (partialPaidByInstallment.get(installment.financingInstallmentNumber) ?? 0)
        : 0;
    // Contrato SAC: cada parcela tem o SEU valor de contrato. Ler o valor mensal aqui
    // chamaria de "encargo" a diferença entre a parcela 300 e a de referência.
    const contractAmount = installment.financingInstallmentNumber != null
        ? getContractInstallmentAmount(curve, installment.financingInstallmentNumber)
        : roundCurrency(curve.monthlyAmount);
    const baseAmount = getInstallmentBaseAmount(contractAmount, paidAmount);

    return {
        contractAmount,
        paidAmount,
        baseAmount,
        // Encargo é só o que passa da base; valor MENOR que a base é antecipação com
        // desconto (ajuste individual antigo), não acréscimo — aí fica 0.
        chargesAmount: Math.max(0, roundCurrency(installment.amount - baseAmount)),
    };
}
